import type {
	MealDate,
	MealPlanDay,
	PrimaryCarb,
	PrimaryProtein,
} from '@hewey/shared'
import { daysBetween, isWeekend } from './dates.ts'

// A title containing any of these isn't a meal to schedule. Matched word by
// word, so "leftover korma" and "chicken takeaway" are out along with the bare
// words themselves.
const STOP_WORDS = new Set([
	'leftover',
	'leftovers',
	'takeaway',
	'takeaways',
	'takeout',
	'takeouts',
	'out',
	'away',
	'party',
])

const isStopped = (key: string): boolean =>
	key.split(' ').some((w) => STOP_WORDS.has(w))

// Appearances needed before a hand-typed meal becomes a candidate. Low so the
// pool isn't empty in the first couple of months.
const MIN_APPEARANCES = 2

// Appearances at which `weekendRate` is trusted completely. Two appearances on
// a Tuesday are a coincidence, not a preference.
const FULL_CONFIDENCE_AT = 5

// Bounds on the observed interval — two days running would otherwise make a
// meal due again tomorrow.
const MIN_INTERVAL_DAYS = 7
const MAX_INTERVAL_DAYS = 120

// Lowercase, then collapse every run of non-alphanumerics to a single space,
// so punctuation, stray emoji and spacing don't split one meal into several.
export const normaliseTitle = (title: string): string =>
	title
		.toLowerCase()
		.replace(/[^\p{L}\p{N}]+/gu, ' ')
		.trim()

// What the variety rules read off a meal. A tagged recipe fills all three; a
// hand-typed meal fills whatever its title gives away and leaves the rest null.
export type Facets = {
	protein: PrimaryProtein | null
	carb: PrimaryCarb | null
	cuisine: string | null
}

const byWord = <T>(
	groups: ReadonlyArray<readonly [T, ReadonlyArray<string>]>,
): Map<string, T> => {
	const m = new Map<string, T>()
	for (const [value, words] of groups) for (const w of words) m.set(w, value)
	return m
}

// A hand-typed meal has no tags, so without this the variety rules can't see
// it and three pasta dishes can land in a row. Reading the obvious words off
// the title is crude, but it's a closed list and it only ever adds a penalty.
const CARB_WORDS = byWord<PrimaryCarb>([
	[
		'pasta',
		[
			'pasta',
			'spaghetti',
			'bolognaise',
			'bolognese',
			'lasagne',
			'lasagna',
			'penne',
			'macaroni',
			'orzo',
			'tagliatelle',
			'linguine',
			'ravioli',
			'carbonara',
			'gnocchi',
		],
	],
	['rice', ['rice', 'risotto', 'paella', 'biryani', 'pilaf', 'jambalaya']],
	['potato', ['potato', 'potatoes', 'chips', 'mash', 'wedges', 'jacket']],
	['noodles', ['noodles', 'noodle', 'ramen', 'udon']],
	[
		'bread_wrap',
		[
			'wrap',
			'wraps',
			'taco',
			'tacos',
			'burrito',
			'quesadilla',
			'fajitas',
			'pizza',
			'burger',
			'burgers',
			'sandwich',
			'sandwiches',
			'toastie',
			'baguette',
		],
	],
	['grains', ['couscous', 'quinoa', 'bulgur', 'barley']],
])

const PROTEIN_WORDS = byWord<PrimaryProtein>([
	['chicken', ['chicken']],
	[
		'red_meat',
		[
			'beef',
			'mince',
			'steak',
			'lamb',
			'pork',
			'sausage',
			'sausages',
			'bacon',
			'gammon',
			'meatball',
			'meatballs',
			'bolognaise',
			'bolognese',
		],
	],
	[
		'fish_white',
		['fish', 'salmon', 'cod', 'haddock', 'tuna', 'prawn', 'prawns'],
	],
	[
		'beans_legumes',
		[
			'beans',
			'bean',
			'lentil',
			'lentils',
			'dal',
			'dhal',
			'chickpea',
			'chickpeas',
			'falafel',
		],
	],
])

// Reads a normalised title. The first matching word wins, so a title naming
// two carbs takes the one it leads with. Cuisine is never guessed.
export const inferFacets = (key: string): Facets => {
	const words = key.split(' ')
	let protein: PrimaryProtein | null = null
	let carb: PrimaryCarb | null = null
	for (const w of words) {
		protein ??= PROTEIN_WORDS.get(w) ?? null
		carb ??= CARB_WORDS.get(w) ?? null
	}
	return { protein, carb, cuisine: null }
}

// Every distinct meal the household has planned. Recipe-linked and hand-typed
// days share a key.
export type MealAppearances = {
	key: string
	// The most recent spelling.
	title: string
	// ISO dates, ascending.
	dates: MealDate[]
	weekendCount: number
}

export const buildAppearances = (
	days: MealPlanDay[],
): Map<string, MealAppearances> => {
	const map = new Map<string, MealAppearances>()
	const ascending = [...days].sort((a, b) => a.date.localeCompare(b.date))
	for (const day of ascending) {
		if (!day.title) continue
		const key = normaliseTitle(day.title)
		if (key.length === 0) continue
		const seen = map.get(key)
		if (seen) {
			seen.title = day.title
			seen.dates.push(day.date)
			if (isWeekend(day.date)) seen.weekendCount += 1
		} else {
			map.set(key, {
				key,
				title: day.title,
				dates: [day.date],
				weekendCount: isWeekend(day.date) ? 1 : 0,
			})
		}
	}
	return map
}

// A hand-typed meal with enough history to be suggested. It has no tags; these
// fields are what it is scored on instead.
export type HistoryCandidate = {
	key: string
	title: string
	count: number
	lastPlanned: MealDate
	// Share of past appearances that fell on Sat/Sun.
	weekendRate: number
	// 0–1 weighting for `weekendRate`, from the number of appearances.
	// Without it a meal seen twice is placed as confidently as one seen
	// twenty times.
	confidence: number
	// Mean days between appearances.
	intervalDays: number
	facets: Facets
}

const clamp = (n: number, min: number, max: number): number =>
	Math.min(max, Math.max(min, n))

// `claimed` holds the normalised title of every recipe. A matching title
// belongs to the recipe, which has tags and a link — so the hand-typed version
// never becomes a candidate, even when the recipe itself is ineligible.
export const historyCandidates = (
	appearances: Map<string, MealAppearances>,
	claimed: ReadonlySet<string>,
): HistoryCandidate[] => {
	const out: HistoryCandidate[] = []
	for (const m of appearances.values()) {
		if (m.dates.length < MIN_APPEARANCES) continue
		if (isStopped(m.key) || claimed.has(m.key)) continue

		const count = m.dates.length
		const first = m.dates[0]
		const last = m.dates[count - 1]
		if (!first || !last) continue

		out.push({
			key: m.key,
			title: m.title,
			count,
			lastPlanned: last,
			weekendRate: m.weekendCount / count,
			confidence: clamp((count - 1) / (FULL_CONFIDENCE_AT - 1), 0, 1),
			facets: inferFacets(m.key),
			intervalDays: clamp(
				daysBetween(first, last) / (count - 1),
				MIN_INTERVAL_DAYS,
				MAX_INTERVAL_DAYS,
			),
		})
	}
	return out
}
